import SimpleCard from "lib/components/common/simple-card";
import { FC, useEffect, useMemo, useState } from "react";
import "./calendar.scss";
import { Accordion, Button, Col, Form, Row } from "react-bootstrap";
import { convertToYYYYMMDD } from "lib/utils/date-utils";
import SimpleModal from "lib/components/common/simple-modal";
import FlexBox from "lib/components/common/flex-box";
import SimpleDropdown from "lib/components/common/simple-dropdown";
import { UNAVAILABLE_DAYS } from "lib/constants";
import {
  getArrayFrom,
  getItemFromArray,
  getItemsFromArray,
  mergeArrays,
  updateArrayItem,
} from "lib/utils/array-utils";
import RequiredField from "lib/components/common/required-field";
import useFormBinder from "lib/hooks/useFormBinder";
import SimpleDataTable from "lib/components/common/simple-data-table";
import ClinicPreview from "lib/components/clinics/clinic-preview";
import {
  CLINIC_ROOM_COLUMNS,
  CLINIC_WORKING_HOURS_COLUMNS,
} from "lib/constants/table-columns";
import ErrorBox from "lib/components/common/error-box";
import SimpleCalendar from "lib/components/common/calendar/simple-calendar";
import { DateTime } from "luxon";
import SimpleButton from "lib/components/common/simple-button";
import SimpleDocUploader from "lib/components/common/docs/simple-doc-uploader";
import SimpleTextbox from "lib/components/common/simple-textbox";

import { POSITIVE_NUMBER_VALIDATION_SCHEMA } from "lib/constants/validation-constants";
import { DEFAULT_ROOM_DETAILS } from "lib/constants/default-values";
import SimpleSwitch from "lib/components/common/simple-switch";
import SimpleDocumentSlider from "lib/components/common/simple-doc-slider";
import SimpleDocSelector from "lib/components/common/docs/simple-doc-selector";
import { DMSServices } from "lib/services/document-services";

const DEFAULT_UNAVAILABLE_DAY: UnavailableDay = {
  date: "",
  title: "",
  type: UNAVAILABLE_DAYS.UNAVAILABLE,
} as UnavailableDay;

interface ClinicDetailsProps {
  error?: string;
  clinic?: Clinic;
  loading?: boolean;
  imagesLoading?: boolean;
  calendarDetails?: CalendarDetails;
  clinicProfilePicUrl?: string;
  rooms?: RoomDetails[];
  selectedRoom?: RoomDetails;
  //selectedRoomImages?: File[];
  onCancelClick?: () => void;
  onClinicSelected?: (clinicId: string) => void;
  onUpdateClinic?: (
    clinic: Clinic,
    updatedCalendarDetails: CalendarDetails,
    clinicProfilePic?: DMSDocument
  ) => void;
  onUpdateRoom?: (roomId?: string, roomDetails?: RoomDetails) => void;
  onRoomSelected?: (room?: RoomDetails) => void;
}

const ClinicDetails: FC<ClinicDetailsProps> = ({
  clinic,
  calendarDetails,
  clinicProfilePicUrl,
  rooms,
  selectedRoom,
  //selectedRoomImages,
  imagesLoading,
  loading,
  error,
  onClinicSelected,
  onCancelClick,
  onUpdateClinic,
  onUpdateRoom,
  onRoomSelected,
}) => {
  const [unavailableDays, setUnavailableDays] = useState<UnavailableDay[]>(
    mergeArrays(
      calendarDetails?.[0]?.unavailableDates ?? [],
      calendarDetails?.[0]?.holidays ?? []
    )
  );

  const holidays = useMemo(
    () =>
      (unavailableDays ?? [])
        .filter((day) => day.type === UNAVAILABLE_DAYS.HOLIDAY)
        .map((day) => DateTime.fromISO(day.date).toJSDate()),
    [unavailableDays]
  );

  const bookedDays = useMemo(
    () =>
      (unavailableDays ?? [])
        .filter((day) => day.type === UNAVAILABLE_DAYS.UNAVAILABLE)
        .map((day) => DateTime.fromISO(day.date).toJSDate()),
    [unavailableDays]
  );

  const [clinicProfilePic, setClinicProfilePic] = useState<DMSDocument | null>(
    null
  );

  // const [selectedRoom, setSelectedRoom] = useState<RoomDetails>(
  //   DEFAULT_ROOM_DETAILS(`Room-${rooms?.length + 1}`)
  // );

  const [roomImages, setRoomImages] = useState<File[]>([]);

  const {
    values: unAvailableDay,
    setValues: setUnAvailableDay,
    resetValues: resetUnAvailableDay,
    handleChange,
    handleBlur,
  } = useFormBinder<UnavailableDay>(DEFAULT_UNAVAILABLE_DAY);

  const {
    values: clinicDetails,
    handleChange: handleChangeClinic,
    handleBlur: handleBlurClinic,
  } = useFormBinder<Clinic>(clinic);

  const [selectedWorkingHour, setSelectedWorkingHour] =
    useState<WorkingHour | null>(null);

  const {
    values: workingHours,
    handleChange: handleChangeWorkingHours,
    handleBlur: handleBlurWorkingHours,
    errors: workingHoursErrors,
    setValues: setWorkingHours,
  } = useFormBinder<WorkingHour>(selectedWorkingHour);

  const {
    values: roomDetails,
    handleChange: handleChangeRoomDetails,
    setValues: setRoomDetails,
    //resetValues: resetRoomDetails,
  } = useFormBinder<RoomDetails>(DEFAULT_ROOM_DETAILS());

  const [isModalOpen, setModalOpen] = useState<boolean>(false);
  //const [isShowRoomModal, setIsShowRoomModal] = useState<boolean>(false);
  const [isShowDelete, setIsShowDelete] = useState<boolean>(false);

  useEffect(() => {
    if (clinic?.id) {
      onClinicSelected(clinic.id);
    }
  }, []);

  useEffect(() => {
    if (clinicProfilePicUrl) {
      DMSServices.getDocumentFromUrl(
        clinicProfilePicUrl,
        "clinicProfile",
        "image/jpeg"
      ).then((doc) => {
        setClinicProfilePic({
          documentUrl: clinicProfilePicUrl,
          document: doc,
        });
      });
    } else {
      setClinicProfilePic(null);
    }
  }, [clinicProfilePicUrl]);

  useEffect(() => {
    setRoomDetails(selectedRoom ?? DEFAULT_ROOM_DETAILS());
  }, [selectedRoom]);

  useEffect(() => {
    setRoomImages(selectedRoom?.images ?? []);
  }, [selectedRoom?.images]);

  const handleUpdateWorkingHours = (workingHour: WorkingHour) => {
    clinic.availableHours = updateArrayItem(
      clinic.availableHours,
      "day",
      workingHour.day,
      workingHour
    );
    setSelectedWorkingHour(null);
  };

  const handleDateClick = (date: Date) => {
    const foundUnavailableDay = getItemFromArray(
      unavailableDays,
      "date",
      convertToYYYYMMDD(date)
    );
    if (foundUnavailableDay) {
      setUnAvailableDay(foundUnavailableDay);
      setIsShowDelete(true);
    } else {
      setUnAvailableDay({
        ...unAvailableDay,
        date: convertToYYYYMMDD(date),
        internalDate: date,
      });
      setIsShowDelete(false);
    }

    setModalOpen(true);
  };

  const handleSave = () => {
    // Validate the details based on type

    setUnavailableDays([...unavailableDays, unAvailableDay]);
    resetUnAvailableDay(DEFAULT_UNAVAILABLE_DAY);
    setModalOpen(false);
  };

  const handleDelete = () => {
    setUnavailableDays(
      unavailableDays.filter((holiday) => holiday.date !== unAvailableDay.date)
    );
    resetUnAvailableDay(DEFAULT_UNAVAILABLE_DAY);
    setModalOpen(false);
  };

  const getProfilePicUrl = () => {
    if (clinicProfilePic?.documentUrl) {
      return clinicProfilePic.documentUrl;
    }
    if (clinicProfilePic?.document) {
      return URL.createObjectURL(clinicProfilePic.document);
    }
    return null;
  };

  const handleAddRoom = () => {
    //setIsShowRoomModal(false);
    onUpdateRoom?.(selectedRoom?.id, { ...roomDetails, images: roomImages });
  };

  const handleDeleteRoom = () => {
    //setIsShowRoomModal(false);
    //resetRoomDetails();
    onUpdateRoom?.(selectedRoom?.id);
  };
  return (
    <>
      <SimpleCard
        title="Clinic Details"
        className=""
        isShowBackButton
        onBackButtonClick={onCancelClick}
        isLoading={loading}
        action={
          <SimpleButton
            isLoading={loading}
            type="button"
            variant="outline-primary"
            className="px-4"
            onClick={() => {
              // Validate the clinic details
              onUpdateClinic(
                clinicDetails,
                {
                  ...calendarDetails?.[0],
                  holidays: getItemsFromArray(
                    unavailableDays,
                    "type",
                    UNAVAILABLE_DAYS.HOLIDAY
                  ),
                  unavailableDates: getItemsFromArray(
                    unavailableDays,
                    "type",
                    UNAVAILABLE_DAYS.UNAVAILABLE
                  ),
                } as CalendarDetails,
                clinicProfilePic
              );
            }}
          >
            Save clinic
          </SimpleButton>
        }
      >
        {error && <ErrorBox message={error} />}
        <Row>
          <Col sm="6">
            <Accordion defaultActiveKey="0">
              <Accordion.Item eventKey="0">
                <Accordion.Header>
                  <strong>Clinic Information</strong>
                </Accordion.Header>
                <Accordion.Body>
                  <Form>
                    <Form.Group className="form-group">
                      <Form.Label>Name</Form.Label>
                      <Form.Control
                        type="text"
                        id="name"
                        name="name"
                        value={clinicDetails.name}
                        className="form-control"
                        onChange={handleChangeClinic}
                        onBlur={handleBlurClinic}
                        required
                      />
                    </Form.Group>
                    <Form.Group className="form-group">
                      <Form.Label>Address</Form.Label>
                      <Form.Control
                        type="text"
                        id="address"
                        name="address"
                        value={clinicDetails.address}
                        className="form-control"
                        onChange={handleChangeClinic}
                        onBlur={handleBlurClinic}
                        required
                      />
                    </Form.Group>
                    <Form.Group className="form-group">
                      <Form.Label>Postcode</Form.Label>
                      <Form.Control
                        type="text"
                        id="postCode"
                        name="postCode"
                        value={clinicDetails.postCode}
                        className="form-control"
                        onChange={handleChangeClinic}
                        onBlur={handleBlurClinic}
                        required
                      />
                    </Form.Group>
                    <Form.Group className="form-group">
                      <Form.Label>No. of Available Rooms</Form.Label>
                      <Form.Control
                        type="number"
                        id="noOfAvailableRooms"
                        name="noOfAvailableRooms"
                        value={clinicDetails.noOfAvailableRooms}
                        className="form-control"
                        onChange={handleChangeClinic}
                        onBlur={handleBlurClinic}
                        required
                        // disabled
                      />
                    </Form.Group>
                    <Form.Group className="form-group">
                      <Form.Label>Website</Form.Label>
                      <Form.Control
                        type="text"
                        id="websiteURL"
                        name="websiteURL"
                        value={clinicDetails.websiteURL}
                        className="form-control"
                        onChange={handleChangeClinic}
                        onBlur={handleBlurClinic}
                      />
                    </Form.Group>
                    <SimpleTextbox
                      label="Room No. Sequence"
                      name="roomNoSequence"
                      id="roomNoSequence"
                      value={clinicDetails.roomNoSequence}
                      readOnly
                      hidden
                    />
                    <SimpleSwitch
                      label="Enable Session Booking"
                      name="enableSessionBooking"
                      value={clinicDetails?.enableSessionBooking ?? false}
                      onChange={handleChangeClinic}
                      isShowIcons={false}
                    />
                    <SimpleSwitch
                      label="Closed On Sundays"
                      name="sundayClose"
                      value={clinicDetails?.sundayClose ?? false}
                      onChange={handleChangeClinic}
                      isShowIcons={false}
                    />
                    {/* {error && <ErrorBox message={error} />} */}
                  </Form>
                </Accordion.Body>
              </Accordion.Item>
              <Accordion.Item eventKey="1">
                <Accordion.Header>
                  <strong>Clinic Calendar</strong>
                </Accordion.Header>
                <Accordion.Body>
                  <FlexBox justifyContent="center">
                    <SimpleCalendar
                      isEditable
                      unavailableDays={unavailableDays}
                      //holidays={holidays}
                      //closedDays={bookedDays}
                      onChange={handleDateClick}
                    />
                  </FlexBox>
                </Accordion.Body>
              </Accordion.Item>
              <Accordion.Item eventKey="2">
                <Accordion.Header>
                  <strong>Clinic Working Hours</strong>
                </Accordion.Header>
                <Accordion.Body>
                  <SimpleDataTable
                    columns={CLINIC_WORKING_HOURS_COLUMNS}
                    data={clinic.availableHours}
                    isShowPagination={false}
                    onRowClick={(row) => {
                      //setIsShowEditWorkingHours(true);
                      setWorkingHours(row as WorkingHour);
                      setSelectedWorkingHour(row as WorkingHour);
                    }}
                  />
                </Accordion.Body>
              </Accordion.Item>
              <Accordion.Item eventKey="3">
                <Accordion.Header>
                  <strong>Clinic Picture</strong>
                </Accordion.Header>
                <Accordion.Body>
                  <SimpleDocUploader
                    type="image"
                    doc={clinicProfilePic?.document}
                    docUrl={clinicProfilePic?.documentUrl}
                    onChange={(doc) =>
                      setClinicProfilePic({
                        ...clinicProfilePic,
                        document: doc?.[0],
                      })
                    }
                    onDelete={() => setClinicProfilePic(null)}
                  />
                </Accordion.Body>
              </Accordion.Item>
              <Accordion.Item eventKey="4">
                <Accordion.Header>
                  <FlexBox justifyContent="start" alignItems="center">
                    <strong>Clinic Rooms</strong>
                  </FlexBox>
                </Accordion.Header>
                <Accordion.Body>
                  <SimpleDataTable
                    data={rooms}
                    columns={CLINIC_ROOM_COLUMNS}
                    title="Rooms"
                    isShowPagination={true}
                    onRowClicked={(row: RoomDetails) => {
                      onRoomSelected?.(row);
                    }}
                    action={
                      <SimpleButton
                        variant="outline-primary"
                        className="px-3"
                        onClick={() => {
                          onRoomSelected?.(
                            DEFAULT_ROOM_DETAILS(`${rooms?.length + 1}`)
                          ); // <-- New room
                        }}
                      >
                        Add Rooms
                      </SimpleButton>
                    }
                  />
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
          </Col>
          <Col sm="6">
            <ClinicPreview
              holidays={holidays}
              bookedDays={bookedDays}
              clinic={clinic}
              clinicCalendar={calendarDetails}
              clinicWorkingHours={clinic.availableHours}
              unavailableDays={unavailableDays}
              clinicProfilePicUrl={getProfilePicUrl()}
            />
          </Col>
        </Row>
      </SimpleCard>
      <SimpleModal
        open={isModalOpen}
        close={() => setModalOpen(false)}
        size="sm"
        title="Set date as"
      >
        <Form>
          <SimpleDropdown
            id="type"
            name="type"
            options={getArrayFrom<string>(UNAVAILABLE_DAYS, true)}
            values={getArrayFrom<string>(UNAVAILABLE_DAYS)}
            selectedValue={unAvailableDay?.type}
            onChange={handleChange}
          />
          {unAvailableDay?.type === UNAVAILABLE_DAYS.HOLIDAY && (
            <Form.Group>
              <Form.Label>
                Description <RequiredField />
              </Form.Label>
              <Form.Control
                type="text"
                name="title"
                id="title"
                value={unAvailableDay?.title}
                className="form-control"
                onChange={handleChange}
                onBlur={handleBlur}
                required
              />
            </Form.Group>
          )}
          <FlexBox justifyContent="between">
            {isShowDelete && (
              <Button onClick={handleDelete} variant="light" className="mt-3">
                Delete
              </Button>
            )}
            <Button onClick={handleSave} className="mt-3">
              Save
            </Button>
          </FlexBox>
        </Form>
      </SimpleModal>
      <SimpleModal
        open={!!selectedWorkingHour}
        close={() => setSelectedWorkingHour(null)}
        size="sm"
        title={`Update working hours for ${workingHours?.day}`}
      >
        <Form>
          <Form.Group>
            <Form.Label>
              From <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="time"
              id="fromTime"
              //name="fromTime"
              value={workingHours?.fromTime}
              onChange={handleChangeWorkingHours}
              onBlur={handleBlurWorkingHours}
              isInvalid={!!workingHoursErrors}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>
              To <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="time"
              id="toTime"
              //name="toTime"
              value={workingHours?.toTime}
              onChange={handleChangeWorkingHours}
              onBlur={handleBlurWorkingHours}
              isInvalid={!!workingHoursErrors}
            />
          </Form.Group>
          <Button
            type="button"
            variant="primary"
            className="mt-3"
            onClick={() => handleUpdateWorkingHours(workingHours)}
          >
            Update
          </Button>
        </Form>
      </SimpleModal>
      <SimpleModal
        loading={loading || imagesLoading}
        open={!!selectedRoom}
        close={() => {
          onRoomSelected?.(null);
        }}
        error={error}
        size="lg"
        title={`${selectedRoom?.id ? "Update" : "Add"} Room`}
        footer={
          <FlexBox
            justifyContent={roomDetails?.id ? "between" : "end"}
            className=""
          >
            {roomDetails?.id && (
              <SimpleButton
                variant="light"
                type="button"
                onClick={handleDeleteRoom}
              >
                Delete
              </SimpleButton>
            )}
            <SimpleButton
              variant="primary"
              type="button"
              onClick={handleAddRoom}
              isLoading={loading}
            >
              {roomDetails?.id ? "Update" : "Add"}
            </SimpleButton>
          </FlexBox>
        }
      >
        <Form>
          <Row>
            <Col sm="6">
              <fieldset>
                <legend>Room details</legend>

                <SimpleTextbox
                  prefix="Room"
                  //label="Room Number"
                  name="roomNumber"
                  value={roomDetails?.roomNumber}
                  onChange={handleChangeRoomDetails}
                  required
                  type="number"
                  placeholder="Enter room name"
                  validationSchema={POSITIVE_NUMBER_VALIDATION_SCHEMA}
                />

                <SimpleTextbox
                  type="number"
                  label="Price Per Day"
                  name="pricePerDay"
                  value={roomDetails?.pricePerDay ?? ""}
                  onChange={handleChangeRoomDetails}
                  required
                  placeholder="Enter price per day"
                  validationSchema={POSITIVE_NUMBER_VALIDATION_SCHEMA}
                />

                <SimpleSwitch
                  label="Is Room Active"
                  name="isActive"
                  value={roomDetails?.isActive ?? false}
                  onChange={handleChangeRoomDetails}
                  isShowIcons={false}
                />
              </fieldset>
            </Col>
            <Col sm="6">
              <fieldset>
                <legend>Room Images</legend>
                <SimpleDocSelector
                  isClearOnSelect
                  isSelectMultiple
                  docType="IMAGES"
                  docUrl={null}
                  onChange={(files) => {
                    if (!files) return;
                    setRoomImages([...roomImages, ...files]);
                  }}
                />

                <SimpleDocumentSlider
                  isLoading={imagesLoading}
                  label="Preview"
                  className="mt-3"
                  documents={roomImages}
                  onRemove={(index) => {
                    const newImages = roomImages.filter(
                      (_, idx) => idx !== index
                    );
                    setRoomImages(newImages);
                  }}
                />
              </fieldset>
            </Col>
          </Row>
        </Form>
      </SimpleModal>
    </>
  );
};
export default ClinicDetails;
